import React, { useState } from 'react';
import { Sparkles, Check } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { BACKEND_URL } from '../config';

/**
 * Lightweight email-capture modal used wherever a feature is gated as
 * "Coming soon" (currently Experience Montreal). Posts to
 * POST /api/waitlist/signup which is idempotent on (feature, email).
 */
const WaitlistModal = ({ open, onOpenChange, feature = 'experience_montreal', title, description }) => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const reset = () => {
    setEmail('');
    setSubmitting(false);
    setDone(false);
    setError('');
  };

  const handleClose = (next) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/waitlist/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), feature }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Could not save your email. Try again.');
      }
      setDone(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md" data-testid="waitlist-modal">
        <DialogHeader>
          <div className="w-12 h-12 mb-3 rounded-full bg-rose-100 flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-rose-600" />
          </div>
          <DialogTitle>{title || 'Be the first to know'}</DialogTitle>
          <DialogDescription>
            {description || "Drop your email and we'll let you know the moment Experience Montreal goes live."}
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="py-4 flex flex-col items-center text-center" data-testid="waitlist-success">
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mb-3">
              <Check className="w-6 h-6 text-green-600" />
            </div>
            <p className="text-gray-900 font-semibold mb-1">You&apos;re on the list</p>
            <p className="text-sm text-gray-500">We&apos;ll email you the moment it launches.</p>
            <Button
              type="button"
              className="mt-5 bg-rose-600 hover:bg-rose-700 text-white"
              onClick={() => handleClose(false)}
              data-testid="waitlist-close-btn"
            >
              Done
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3 pt-2">
            <Input
              type="email"
              required
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="waitlist-email-input"
              disabled={submitting}
            />
            {error && (
              <p className="text-sm text-red-600" data-testid="waitlist-error">{error}</p>
            )}
            <Button
              type="submit"
              className="w-full bg-rose-600 hover:bg-rose-700 text-white"
              disabled={submitting}
              data-testid="waitlist-submit-btn"
            >
              {submitting ? 'Saving…' : 'Notify me'}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default WaitlistModal;
